import { CliError, EXIT_USAGE } from "../../../common.js";
import {
  listActionLocationsV4,
  listEpisodeLocationsV4,
  listSceneLocationsV4,
  type V4Address,
} from "./address.js";
import type { ScriptV4 } from "./types.js";

function ordinalError(raw: string, detail: string): never {
  throw new CliError(detail, {
          exitCode: EXIT_USAGE,
          errorCode: "ORDINAL_ANCHOR_INVALID",
          data: { expected: [
      "@ep:N | @ep:N/scn:N | @ep:N/scn:N/ac:N | @scn:N | @scn:N/ac:N | @ac:N",
    ], actual: [raw] },
        });
}

function positive(raw: string, anchor: string): number {
  if (!/^[1-9]\d*$/.test(raw)) ordinalError(anchor, "Ordinal positions are 1-based positive integers.");
  return Number(raw);
}

export function resolveV4Ordinal(script: ScriptV4, raw: string): V4Address {
  let match = /^@ep:(\d+)$/.exec(raw);
  if (match) {
    const row = listEpisodeLocationsV4(script)[positive(match[1]!, raw) - 1];
    if (!row) ordinalError(raw, "Episode ordinal is outside the current script.");
    return { kind: "episode", id: row.episode.episode_id };
  }
  match = /^@ep:(\d+)\/scn:(\d+)$/.exec(raw);
  if (match) {
    const episode = listEpisodeLocationsV4(script)[positive(match[1]!, raw) - 1];
    const scene = episode?.episode.scenes[positive(match[2]!, raw) - 1];
    if (!episode || !scene) ordinalError(raw, "Episode-local scene ordinal is outside the current script.");
    return { kind: "scene", id: scene.scene_id };
  }
  match = /^@ep:(\d+)\/scn:(\d+)\/ac:(\d+)$/.exec(raw);
  if (match) {
    const episode = listEpisodeLocationsV4(script)[positive(match[1]!, raw) - 1];
    const scene = episode?.episode.scenes[positive(match[2]!, raw) - 1];
    const action = scene?.actions[positive(match[3]!, raw) - 1];
    if (!episode || !scene || !action) ordinalError(raw, "Episode-local action ordinal is outside the current script.");
    return { kind: "action", id: action.action_id };
  }
  match = /^@scn:(\d+)$/.exec(raw);
  if (match) {
    const row = listSceneLocationsV4(script)[positive(match[1]!, raw) - 1];
    if (!row) ordinalError(raw, "Global scene ordinal is outside the current script.");
    return { kind: "scene", id: row.scene.scene_id };
  }
  match = /^@scn:(\d+)\/ac:(\d+)$/.exec(raw);
  if (match) {
    const row = listSceneLocationsV4(script)[positive(match[1]!, raw) - 1];
    const action = row?.scene.actions[positive(match[2]!, raw) - 1];
    if (!row || !action) ordinalError(raw, "Scene-local action ordinal is outside the current script.");
    return { kind: "action", id: action.action_id };
  }
  match = /^@ac:(\d+)$/.exec(raw);
  if (match) {
    const row = listActionLocationsV4(script)[positive(match[1]!, raw) - 1];
    if (!row) ordinalError(raw, "Global action ordinal is outside the current script.");
    return { kind: "action", id: row.action.action_id };
  }
  return ordinalError(raw, "Ordinal anchor syntax is not supported.");
}
